import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Self,
} from '@angular/core';
import {
  AsyncValidator,
  ControlContainer,
  ControlValueAccessor,
  FormGroupDirective,
  FormGroupName,
  NgControl,
} from '@angular/forms';
import { DepreciationMethodSettings } from '@assetaccountant/assets/models/depreciation-method-settings';
import { toUniversalDateString } from '@assetaccountant/core';
import { PeriodDepreciationCalculated } from '@assetaccountant/core/models/enum/period-depreciation-calculated';
import { getValidRateRequest } from '@assetaccountant/core/models/get-rate-api-model-request';
import { GetRateFromEndDateApiModelResponse } from '@assetaccountant/core/models/get-rate-api-model-response';
import { DepreciationService } from '@assetaccountant/core/services/depreciation.service';
import { isNumeric, roundTo } from '@assetaccountant/core/utils/numeric.utils';
import {
  NgControlDirective,
  TypedAsyncValidatorFn,
  TypedFormControl,
  emptyValue,
} from '@assetaccountant/core/utils/typed-forms';
import moment from 'moment';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  AssetRegimeFormGroup,
  getDepreciationMethodId,
} from '../depreciation-method-selector.component';

@Component({
  selector: 'app-autocalc-life-end-date',
  templateUrl: './autocalc-life-end-date.component.html',
  styleUrls: ['./autocalc-life-end-date.component.scss'],
  standalone: false,
})
export class AutocalcLifeEndDateComponent
  implements OnInit, OnDestroy, ControlValueAccessor, AsyncValidator
{
  model: string | null = null;
  lifeActual: number | null = null;

  pendingLifeValue: number | null = null;

  periodCalculation?: PeriodDepreciationCalculated;

  @Input() firstUseDate: string;
  @Input() effectiveDate?: string;
  @Input() set settings(value: DepreciationMethodSettings | undefined) {
    this.isRateApplicable = value?.isRateApplicable ?? false;
    this.isRateEditable = value?.isRateEditable ?? false;
    this.autoCalculateRate = value?.autoCalculateRate ?? false;
  }

  @Output() remainingLifeChange = new EventEmitter<number | undefined>();

  isLoading = false;
  isDisabled = false;

  isRateApplicable = false;
  isRateEditable = false;
  autoCalculateRate = false;

  get form() {
    return this.controlContainer.control as AssetRegimeFormGroup;
  }
  get rateControl() {
    return this.form.controls.rate;
  }

  _onChange?: (val: number | null) => void;
  _onTouched?: () => void;

  constructor(
    @Inject(ControlContainer) private controlContainer: FormGroupDirective | FormGroupName,
    @Self() @Inject(NgControl) public ngControl: NgControlDirective,
    private depreciationService: DepreciationService,
  ) {
    this.ngControl.valueAccessor = this;
  }

  ngOnInit() {
    if (!this.model && !this.pendingLifeValue && isNumeric(this.lifeActual) && this.firstUseDate) {
      this.updateEndDateFromLife(this.lifeActual, this.firstUseDate);
    }

    setTimeout(() => {
      this.ngControl.control?.setAsyncValidators(c => this.validate(c as TypedFormControl<number>));
      this.ngControl.control?.updateValueAndValidity();
    });
  }

  ngOnDestroy() {
    this.ngControl.control.clearAsyncValidators();
  }

  writeValue(value: number | null) {
    this.model = null;
    this.pendingLifeValue = null;
    this.lifeActual = null;

    if (isNumeric(value)) {
      const life = +value;
      this.lifeActual = life;

      if (this.firstUseDate) {
        this.updateEndDateFromLife(life, this.firstUseDate);
      }
    }
  }

  onChange(value: string | null) {
    this.model = value || null;
    const newLifeGuess = this.model
      ? this.getLifeGuessFromEndDate(this.model, this.firstUseDate)
      : null;
    if (this.pendingLifeValue !== newLifeGuess) {
      this.pendingLifeValue = newLifeGuess;
      this.lifeActual = null;
      this._onChange?.(this.pendingLifeValue);
    }
    this._onTouched?.();
  }

  validate: TypedAsyncValidatorFn<TypedFormControl<number>> = control => {
    const lifeValue = control.value;
    return this.getRateAndLife(this.model).pipe(
      map(response => {
        if (!response) {
          return null;
        }

        // Is there a new request pending?
        if (this.pendingLifeValue !== lifeValue) {
          return null;
        }

        const { rate, life, remainingLife } = response;

        // Update life if needed
        if (this.lifeActual !== life) {
          this.lifeActual = life;
          this._onChange?.(this.lifeActual);
        }

        // Update remaining life
        this.remainingLifeChange.emit(remainingLife);

        // Update rate if it should be calculated
        if (this.autoCalculateRate && this.form) {
          this.updateRateControl(rate);

          if (!isNumeric(rate)) {
            return {
              remainingLife: {
                life,
                remainingLife,
              },
            };
          }
        }

        return null;
      }),
    );
  };

  getRateAndLife(endDate: string | null): Observable<null | GetRateFromEndDateApiModelResponse> {
    const { depreciationMethodId } = getDepreciationMethodId(this.form.value.depreciationMethodId);
    const { regimeId } = this.form.value;
    if (!depreciationMethodId) {
      return of(null);
    }

    const request = getValidRateRequest({
      regimeId,
      depreciationMethodId: +depreciationMethodId,
      endDate,
      firstUseDate: this.firstUseDate,
      date: this.effectiveDate,
    });

    if (!request) {
      return of(null);
    }

    return this.depreciationService.getRate(request).pipe(
      map(response => {
        if (!response) {
          return null;
        }

        const { rate, life, remainingLife } = response;

        const roundedLife = roundTo(life, 4);
        const roundedRemainingLife = remainingLife && roundTo(remainingLife, 4);

        return {
          rate,
          life: roundedLife,
          remainingLife: roundedRemainingLife,
        };
      }),
    );
  }

  private updateEndDateFromLife(life: number, firstUseDate: string) {
    this.pendingLifeValue = life;

    const equalYearsEndDate = toUniversalDateString(
      moment.utc(firstUseDate).add({ years: life, days: -1 }),
    );
    const equalDaysEndDate = toUniversalDateString(
      moment.utc(firstUseDate).add({ days: life * 365 - 1 }),
    );

    if (equalYearsEndDate && equalDaysEndDate) {
      this.isLoading = true;

      return combineLatest(
        [
          {
            periodCalculated: PeriodDepreciationCalculated.Daily_YearsAreEqual,
            endDate: equalYearsEndDate,
          },
          {
            periodCalculated: PeriodDepreciationCalculated.Daily_DaysAreEqual,
            endDate: equalDaysEndDate,
          },
        ].map(x =>
          this.getRateAndLife(x.endDate).pipe(
            catchError(() => of(null)),
            map(response => ({ ...x, response })),
          ),
        ),
      ).subscribe(results => {
        this.isLoading = false;

        let setPeriodCalculation = false;
        results.sort((a, b) => {
          if (a.response == null || b.response == null) {
            if (a.response == null && b.response == null) {
              return 0;
            }

            return a.response == null ? 1 : -1;
          }

          const aDiffInYears = roundTo(life - a.response.life, 4);
          const bDiffInYears = roundTo(life - b.response.life, 4);

          const result = Math.abs(aDiffInYears) - Math.abs(bDiffInYears);
          if (result !== 0) {
            setPeriodCalculation = true;
          }
          return result;
        });

        const { periodCalculated, endDate, response } = results[0];

        this.model = endDate;
        if (setPeriodCalculation) {
          this.periodCalculation = periodCalculated;
        }

        this.pendingLifeValue = this.getLifeGuessFromEndDate(endDate, firstUseDate);

        this.updateRateControl(response?.rate);

        this._onChange?.(life);
      });
    } else {
      this.model = null;
    }
  }

  private getLifeGuessFromEndDate(endDate: string, firstUseDate: string) {
    const momDayAfterEndDate = moment.utc(endDate).add({ days: 1 });
    switch (this.periodCalculation) {
      case PeriodDepreciationCalculated.Daily_DaysAreEqual:
        return momDayAfterEndDate.diff(moment.utc(firstUseDate), 'days', true) / 365;
      default:
        return momDayAfterEndDate.diff(moment.utc(firstUseDate), 'years', true);
    }
  }

  private updateRateControl(rate: number | undefined) {
    if (!this.isRateApplicable || !this.autoCalculateRate) {
      return;
    }

    if (!this.isRateEditable || !this.rateControl.value || this.rateControl.pristine) {
      this.rateControl.setValue(rate || emptyValue<number>());
      this.rateControl.markAsPristine();
      this.rateControl.markAsTouched();
    }
  }

  registerOnChange(fn: (val: number | null) => void): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
}
